/**
 * Drawer Coordinator
 * 
 * Manages sequential opening/closing of header drawers (search, account, cart, menu).
 * When a drawer trigger is clicked and another drawer is open, it closes the open drawer
 * first, waits for the close animation to complete, then opens the newly clicked drawer.
 */

class DrawerCoordinator {
  constructor() {
    this.pendingOpen = null;
    this.isTransitioning = false;
    
    this.drawerConfigs = [
      {
        name: 'search',
        componentSelector: 'search-drawer-component#search-modal',
        triggerSelectors: [
          'button[on\\:click*="#search-modal/toggleDialog"]',
          '.search-modal__button',
          'button.search-modal__button',
          '.search-action button',
          'search-button button'
        ],
        isOpen: (component) => {
          const dialog = component?.querySelector('dialog');
          return dialog?.open === true;
        },
        close: async (component) => {
          if (component?.closeDialog) {
            await component.closeDialog();
          }
        },
        open: (component) => {
          if (component?.showDialog) {
            component.showDialog();
          }
        }
      },
      {
        name: 'account',
        componentSelector: 'account-drawer-component',
        triggerSelectors: [
          '.account-button[on\\:click*="/toggleDialog"]',
          'button.account-button',
          '.account-button',
          'button[aria-haspopup="dialog"][class*="account"]'
        ],
        isOpen: (component) => {
          const dialog = component?.querySelector('dialog');
          return dialog?.open === true;
        },
        close: async (component) => {
          if (component?.closeDialog) {
            await component.closeDialog();
          }
        },
        open: (component) => {
          if (component?.showDialog) {
            component.showDialog();
          }
        }
      },
      {
        name: 'cart',
        componentSelector: 'cart-drawer-component',
        triggerSelectors: [
          '.cart-drawer__trigger[on\\:click*="/toggle"]',
          'button.cart-drawer__trigger',
          '.cart-drawer__trigger',
          'button[aria-haspopup="dialog"][class*="cart"]',
          'cart-drawer-component button[on\\:click*="/toggle"]'
        ],
        isOpen: (component) => {
          const dialog = component?.querySelector('dialog');
          return dialog?.open === true;
        },
        close: async (component) => {
          if (component?.close) {
            await component.close();
          }
        },
        open: (component) => {
          if (component?.open) {
            component.open();
          }
        }
      },
      {
        name: 'menu',
        componentSelector: 'header-drawer',
        triggerSelectors: [
          'button.header__icon--menu',
          'button.header__icon--summary',
          'button[on\\:click*="/toggle"][class*="header__icon"]',
          'header-drawer button[aria-haspopup="dialog"]'
        ],
        isOpen: (component) => {
          const dialog = component?.querySelector('dialog');
          return dialog?.open === true;
        },
        close: async (component) => {
          if (component?.closeDialog) {
            await component.closeDialog();
          }
        },
        open: (component) => {
          if (component?.showDialog) {
            component.showDialog();
          }
        }
      }
    ];

    this.init();
  }

  init() {
    // Listen for clicks on drawer triggers with capture: true to intercept before component handlers
    document.addEventListener('click', this.handleTriggerClick.bind(this), { capture: true });
    
    // Also listen for dialog:open events to close other drawers when one opens
    // This handles cases where drawers open programmatically or timing issues occur
    document.addEventListener('dialog:open', this.handleDrawerOpen.bind(this), { capture: true });
    
    // Listen for dialog:close events to update backdrop visibility
    document.addEventListener('dialog:close', this.handleDrawerClose.bind(this), { capture: true });
    
    // Handle clicks on the shared backdrop to close the currently open drawer
    const sharedBackdrop = document.getElementById('drawer-coordinator-backdrop');
    if (sharedBackdrop) {
      sharedBackdrop.addEventListener('click', this.handleBackdropClick.bind(this));
    } else {
      // If backdrop doesn't exist yet, wait for it
      const observer = new MutationObserver(() => {
        const backdrop = document.getElementById('drawer-coordinator-backdrop');
        if (backdrop) {
          backdrop.addEventListener('click', this.handleBackdropClick.bind(this));
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
    
    // Periodically check backdrop visibility (fallback)
    setInterval(() => this.updateBackdropVisibility(), 100);
  }

  /**
   * Checks if an element is part of the menu button
   */
  isMenuButtonElement(element) {
    if (!element) return false;
    
    // Check if it's a button with menu classes
    if (element.tagName === 'BUTTON') {
      if (element.classList.contains('header__icon--summary') || 
          element.classList.contains('header__icon--menu')) {
        return true;
      }
    }
    
    // Check if it's inside a menu button
    const menuButton = element.closest('button.header__icon--summary, button.header__icon--menu');
    if (menuButton) {
      const headerDrawer = menuButton.closest('header-drawer');
      if (headerDrawer) {
        return true;
      }
    }
    
    // Check if it's inside header-drawer and part of the menu trigger
    const headerDrawer = element.closest('header-drawer');
    if (headerDrawer) {
      const button = headerDrawer.querySelector('button.header__icon--summary, button.header__icon--menu');
      if (button && button.contains(element)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Gets the drawer configuration for a given trigger element
   */
  getDrawerConfigForTrigger(triggerElement) {
    // Special handling for menu drawer - check multiple ways
    // First, check if element is a button inside header-drawer
    if (triggerElement.tagName === 'BUTTON') {
      const headerDrawer = triggerElement.closest('header-drawer');
      if (headerDrawer) {
        // Check if this button has the menu classes or attributes
        if (triggerElement.classList.contains('header__icon--summary') || 
            triggerElement.classList.contains('header__icon--menu') ||
            triggerElement.hasAttribute('aria-haspopup') ||
            triggerElement.getAttribute('aria-haspopup') === 'dialog') {
          const menuConfig = this.drawerConfigs.find(config => config.name === 'menu');
          if (menuConfig) return menuConfig;
        }
      }
    }
    
    // Check if element is inside a menu button
    const menuButton = triggerElement.closest('button.header__icon--summary, button.header__icon--menu');
    if (menuButton) {
      const headerDrawer = menuButton.closest('header-drawer');
      if (headerDrawer) {
        const menuConfig = this.drawerConfigs.find(config => config.name === 'menu');
        if (menuConfig) return menuConfig;
      }
    }
    
    // Check if element is inside header-drawer and is part of the menu trigger area
    const headerDrawer = triggerElement.closest('header-drawer');
    if (headerDrawer) {
      const button = headerDrawer.querySelector('button.header__icon--summary, button.header__icon--menu');
      if (button) {
        // Check if the clicked element is the button or inside it
        if (triggerElement === button || button.contains(triggerElement)) {
          const menuConfig = this.drawerConfigs.find(config => config.name === 'menu');
          if (menuConfig) return menuConfig;
        }
      }
    }
    
    // Also check if the element itself matches menu button selectors
    if (triggerElement.matches && (
      triggerElement.matches('button.header__icon--summary') ||
      triggerElement.matches('button.header__icon--menu') ||
      triggerElement.matches('button[aria-haspopup="dialog"][class*="header__icon"]')
    )) {
      const menuConfig = this.drawerConfigs.find(config => config.name === 'menu');
      if (menuConfig) return menuConfig;
    }

    // For other drawers, check if element matches any of the trigger selectors
    return this.drawerConfigs.find(config => {
      // Skip menu config as we handled it above
      if (config.name === 'menu') return false;

      for (const selector of config.triggerSelectors) {
        // Check if the element itself matches
        if (triggerElement.matches && triggerElement.matches(selector)) {
          return true;
        }
        // Also check if the trigger is inside a matching element
        if (triggerElement.closest) {
          const closestMatch = triggerElement.closest(selector);
          if (closestMatch !== null) {
            return true;
          }
        }
        // Check parent elements as well
        let parent = triggerElement.parentElement;
        while (parent && parent !== document.body) {
          if (parent.matches && parent.matches(selector)) {
            return true;
          }
          parent = parent.parentElement;
        }
      }
      return false;
    });
  }

  /**
   * Gets the component element for a drawer configuration (first match)
   */
  getDrawerComponent(config) {
    return document.querySelector(config.componentSelector);
  }

  /**
   * Gets the drawer component for a given config, finding the one containing the clicked element
   * This is important for drawers that might have multiple instances (like menu drawer)
   */
  getDrawerComponentForClick(config, clickedElement) {
    // For menu drawer, find the specific instance that contains the clicked element
    if (config.name === 'menu' && clickedElement) {
      const headerDrawer = clickedElement.closest('header-drawer');
      if (headerDrawer) {
        return headerDrawer;
      }
    }
    
    // For other drawers or fallback, get the first match
    return document.querySelector(config.componentSelector);
  }

  /**
   * Gets all component elements for a drawer configuration
   */
  getAllDrawerComponents(config) {
    return Array.from(document.querySelectorAll(config.componentSelector));
  }

  /**
   * Finds which drawer is currently open
   */
  findOpenDrawer() {
    for (const config of this.drawerConfigs) {
      // Check all instances of this drawer type (in case there are multiple)
      const components = this.getAllDrawerComponents(config);
      for (const component of components) {
        if (component && config.isOpen(component)) {
          return { config, component };
        }
      }
    }
    return null;
  }

  /**
   * Handles dialog:close events to update backdrop visibility
   */
  handleDrawerClose(event) {
    // Use requestAnimationFrame to ensure state is updated
    requestAnimationFrame(() => {
      this.updateBackdropVisibility();
    });
  }

  /**
   * Handles dialog:open events to close other drawers when one opens
   * This is a safety mechanism to catch race conditions or programmatic openings
   */
  handleDrawerOpen(event) {
    // Skip if we're in the middle of a managed transition (handleTriggerClick is handling it)
    if (this.isTransitioning) {
      return;
    }

    // Use requestAnimationFrame to ensure the dialog.open property is set
    requestAnimationFrame(async () => {
      // Find which drawer just opened
      const openingDrawer = this.findOpenDrawer();
      
      if (!openingDrawer) return;

      const openingConfig = openingDrawer.config;

      // Close all other drawers (but keep backdrop visible since we're switching)
      for (const config of this.drawerConfigs) {
        if (config.name === openingConfig.name) continue;

        const components = this.getAllDrawerComponents(config);
        for (const component of components) {
          if (config.isOpen(component)) {
            // Close it without hiding backdrop (will be handled by new drawer opening)
            await config.close(component);
          }
        }
      }
    });
  }

  /**
   * Checks if any drawer is currently open
   */
  hasAnyDrawerOpen() {
    return this.findOpenDrawer() !== null;
  }

  /**
   * Updates the shared backdrop visibility based on whether any drawer is open
   */
  updateBackdropVisibility() {
    const hasOpen = this.hasAnyDrawerOpen();
    const sharedBackdrop = document.getElementById('drawer-coordinator-backdrop');
    
    if (!sharedBackdrop) return;
    
    if (hasOpen) {
      sharedBackdrop.setAttribute('data-drawer-open', '');
    } else {
      sharedBackdrop.removeAttribute('data-drawer-open');
    }
  }

  /**
   * Handles clicks on the shared backdrop to close the currently open drawer
   */
  handleBackdropClick(event) {
    // Only handle clicks directly on the backdrop, not on children
    if (event.target !== event.currentTarget) return;
    
    const openDrawer = this.findOpenDrawer();
    if (openDrawer && openDrawer.component) {
      openDrawer.config.close(openDrawer.component);
    }
  }

  /**
   * Handles click events on drawer triggers
   */
  async handleTriggerClick(event) {
    // Don't interfere if we're already transitioning
    if (this.isTransitioning) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    // Find the drawer config for this trigger - check both the target and its parents
    let clickedConfig = this.getDrawerConfigForTrigger(event.target);
    
    // If not found, try checking the currentTarget or walking up the tree
    if (!clickedConfig && event.currentTarget) {
      clickedConfig = this.getDrawerConfigForTrigger(event.currentTarget);
    }
    
    // Walk up the DOM tree to find the trigger button
    // Special handling for menu button - check if any element in the path is part of menu
    if (!clickedConfig) {
      let element = event.target;
      while (element && element !== document.body) {
        // First check if this is a menu button element
        if (this.isMenuButtonElement(element)) {
          const menuConfig = this.drawerConfigs.find(config => config.name === 'menu');
          if (menuConfig) {
            clickedConfig = menuConfig;
            break;
          }
        }
        
        // Then check for other drawer triggers
        clickedConfig = this.getDrawerConfigForTrigger(element);
        if (clickedConfig) break;
        element = element.parentElement;
      }
    }
    
    if (!clickedConfig) {
      // Not a drawer trigger, allow normal behavior
      return;
    }

    const clickedComponent = this.getDrawerComponentForClick(clickedConfig, event.target);
    if (!clickedComponent) {
      // Component not found, allow normal behavior
      return;
    }

    // Check if the clicked drawer is already open
    const isClickedDrawerOpen = clickedConfig.isOpen(clickedComponent);

    // Find any currently open drawer
    const openDrawer = this.findOpenDrawer();

    // If no drawer is open, allow normal behavior
    if (!openDrawer) {
      return;
    }

    // If clicking the same drawer that's open, allow normal toggle behavior
    if (openDrawer.config.name === clickedConfig.name) {
      return;
    }

    // If clicking a different drawer while one is open, handle sequential close/open
    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Set transitioning flag
    this.isTransitioning = true;

    try {
      // Keep backdrops visible during transition
      this.updateBackdropVisibility();
      
      // Set a flag on body to indicate we're transitioning (for drawer close handlers to check)
      document.body.setAttribute('data-drawer-transitioning', 'true');
      
      // Close the currently open drawer
      // It won't remove fixed positioning because isAnotherDrawerOpening will be true
      // This preserves the scroll position in body.style.top
      await openDrawer.config.close(openDrawer.component);

      // Keep backdrops visible (new drawer will open soon)
      this.updateBackdropVisibility();

      // Wait a bit more to ensure animations are fully complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Open the clicked drawer (only if it wasn't already open)
      // It will read scroll position from body.style.top (which was preserved by the closing drawer)
      if (!isClickedDrawerOpen) {
        clickedConfig.open(clickedComponent);
      }
      
      // Ensure backdrop stays visible after opening
      this.updateBackdropVisibility();
    } finally {
      // Remove transition flag
      document.body.removeAttribute('data-drawer-transitioning');
      // Reset transitioning flag
      this.isTransitioning = false;
      // Final backdrop update
      this.updateBackdropVisibility();
    }
  }
}

// Initialize the drawer coordinator when the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DrawerCoordinator();
  });
} else {
  new DrawerCoordinator();
}
